import React, { createContext, useState, useContext, useEffect } from 'react';
import { configAPI } from '../api/client';

const ConfigContext = createContext();

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig debe usarse dentro de ConfigProvider');
  }
  return context;
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    nombre_negocio: 'Mi Negocio',
    moneda: 'Q',
    iva_porcentaje: 12,
    decimales: 2,
  });
  const [sucursales, setSucursales] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await configAPI.get();
      if (response.data.success) {
        setConfig(response.data.data);
      }
      
      const sucursalesRes = await configAPI.getSucursales();
      if (sucursalesRes.data.success) {
        setSucursales(sucursalesRes.data.data);
      }
      
      const almacenesRes = await configAPI.getAlmacenes();
      if (almacenesRes.data.success) {
        setAlmacenes(almacenesRes.data.data);
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (newConfig) => {
    try {
      const response = await configAPI.update(newConfig);
      if (response.data.success) {
        setConfig(newConfig);
        return { success: true };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const formatCurrency = (amount) => {
    const decimales = parseInt(config.decimales || 2);
    const value = Number.parseFloat(amount || 0);

    try {
      const formatter = new Intl.NumberFormat('es-GT', {
        style: 'currency',
        currency: 'GTQ',
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
      });
      return formatter.format(value);
    } catch (e) {
      return `${config.moneda || 'Q'} ${value.toFixed(decimales)}`;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    
    // Crear fecha. Si viene de MySQL (ej: '2023-11-21 15:30:00'),
    // new Date lo interpreta como local.
    // Pero queremos asegurar que se visualice como fecha de Guatemala.
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) return dateString;
    
    return new Intl.DateTimeFormat('es-GT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Guatemala' // Forzar zona horaria de GT
    }).format(date);
  };

  const value = {
    config,
    sucursales,
    almacenes,
    loading,
    updateConfig,
    loadConfig,
    formatCurrency,
    formatDateTime,
  };

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};
